import classNames from 'classnames';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { CALENDAR_ENTER_MS, CalendarMotion } from './calendar-motion';
import UDDateInput, { parseFromString, UDDateInputProps } from './ud-date-input';

/**
 * Переход «С» → «По»: календарь начала не уходит, а стоит, пока поверх него на том же месте
 * проявляется календарь конца. Глазу это — один календарь, который плавно перекрашивается
 * (гаснут дни до начала, стрелка назад становится неактивной), а не закрытие и открытие.
 */
const HANDOFF_START_MOTION: Partial<CalendarMotion> = { exit: 'hold' };
const HANDOFF_END_MOTION: Partial<CalendarMotion> = { enter: 'fade' };

export interface UDDateRangeValue {
  start: Date | null;
  end: Date | null;
}

export type UDDateRangePart = 'start' | 'end';

/** Пропсы, которые пробрасываются в одно из двух полей: `name`, `onBlur`, `autoComplete`, `id`. */
export type UDDateRangeFieldProps = Partial<
  Omit<UDDateInputProps, 'value' | 'onChange' | 'selected' | 'minDate' | 'maxDate' | 'selectsStart' | 'selectsEnd' | 'pickerRef'>
>;

export interface UDDateRangeInputProps {
  /** Дата или строка ISO — как у `UDDateInput`. */
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  onChange: (value: UDDateRangeValue) => void;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  disabled?: boolean;
  hasErrorStart?: boolean;
  hasErrorEnd?: boolean;
  validStart?: boolean;
  validEnd?: boolean;
  startProps?: UDDateRangeFieldProps;
  endProps?: UDDateRangeFieldProps;
  /**
   * Обёртка каждого поля — подпись, тултип, подсветка. Поле должно остаться последним в обёртке:
   * тире между датами выравнивается по нижнему краю строки.
   */
  renderField?: (part: UDDateRangePart, field: ReactNode) => ReactNode;
  /**
   * Выбор начала в календаре начинает период заново: конец сбрасывается, открывается его календарь,
   * а начало можно выбрать и позже прежнего конца. Нужен там, где неполный период нельзя
   * применять (фильтр аналитики шлёт запрос только по паре). Без него — режим формы: начало не
   * позже конца, конец сохраняется.
   */
  restartOnStartSelect?: boolean;
  className?: string;
}

/**
 * Период «С — По»: два поля с общим календарём-диапазоном (IMGM-1496, вариант R3).
 *
 * Полей два, а не одно с `selectsRange`, — так каждую дату можно ввести с клавиатуры
 * `дд.мм.гггг`, как раньше. Оба календаря знают обе даты (`selectsStart` / `selectsEnd`),
 * поэтому подсвечивают промежуток и будущий промежуток при наведении. После выбора начала в
 * календаре сразу открывается календарь конца. Конец раньше начала недоступен; начало позже
 * конца — тоже, кроме режима `restartOnStartSelect`.
 */
const UDDateRangeInput = (props: UDDateRangeInputProps) => {
  const {
    startDate,
    endDate,
    onChange,
    minDate,
    maxDate,
    disabled,
    hasErrorStart,
    hasErrorEnd,
    validStart,
    validEnd,
    startProps,
    endProps,
    renderField = (_part, field) => field,
    restartOnStartSelect = false,
    className,
  } = props;

  const start = parseFromString(startDate);
  const end = parseFromString(endDate);
  const min = parseFromString(minDate);
  const max = parseFromString(maxDate);
  const endPickerRef = useRef<DatePicker>(null);
  const [isHandoff, setIsHandoff] = useState(false);

  // Режим перехода живёт ровно столько, сколько проявляется календарь конца; дальше — обычные анимации.
  useEffect(() => {
    if (!isHandoff) return undefined;
    const timer = setTimeout(() => setIsHandoff(false), CALENDAR_ENTER_MS * 2);
    return () => clearTimeout(timer);
  }, [isHandoff]);
  const startColumnRef = useRef<HTMLDivElement>(null);
  const endColumnRef = useRef<HTMLDivElement>(null);

  /*
   * Календарь конца встаёт ровно на место календаря начала, а не под своим полем: при переходе
   * «С» → «По» он не прыгает вбок. Поповеры по-прежнему два (у каждого поля свой), поэтому конец
   * сдвигается влево на расстояние между полями. Сдвиг — функция: Popper зовёт её при каждом
   * пересчёте позиции, так что ширина поля, ресайз окна и перенос строки учитываются сами.
   */
  const endPopperModifiers = useMemo(
    () =>
      [
        {
          name: 'offset',
          options: {
            offset: (): [number, number] => {
              const startLeft = startColumnRef.current?.getBoundingClientRect().left ?? 0;
              const endLeft = endColumnRef.current?.getBoundingClientRect().left ?? 0;
              return [startLeft - endLeft, 0];
            },
          },
        },
      ] as UDDateInputProps['popperModifiers'],
    [],
  );

  const handleStartChange = useCallback(
    (date: Date | null, event?: React.SyntheticEvent) => {
      // Клик или Enter в календаре, а не ввод: `onSelect` придёт следом, но к нему пара «новое начало + старый
      // конец» уже ушла бы наружу — и экран успел бы её применить.
      const isCalendarPick = event?.type !== 'change';
      // В режиме формы начало позже конца сюда не попадёт: `maxDate` начала = конец, такой ввод библиотека отбрасывает.
      const keepsEnd = !date || !end || date <= end;
      const restarts = restartOnStartSelect && isCalendarPick;
      onChange({ start: date, end: keepsEnd && !restarts ? end : null });
    },
    [end, onChange, restartOnStartSelect],
  );

  const handleEndChange = useCallback((date: Date | null) => onChange({ start, end: date }), [start, onChange]);

  /*
   * Только выбор в календаре (клик, Enter), не ввод с клавиатуры: печатающего человека не уводим из поля.
   * Календарь конца открывается сразу, в том же обработчике, — календарь начала закрывается в этом же
   * рендере, и пустого кадра между ними нет. Фокус в поле конца — отложенно и дважды: после выбора
   * react-datepicker сам возвращает фокус в поле начала (`sendFocusBackToInput` — `setTimeout` в колбэке
   * `setState`), и одинарный таймер он перебивает.
   */
  const handleStartSelect = useCallback(
    (date: Date) => {
      // Повторный клик по тому же началу `onChange` не вызывает (дата не изменилась) — период начинается заново здесь.
      if (restartOnStartSelect && end) onChange({ start: date, end: null });
      setIsHandoff(true);
      endPickerRef.current?.setOpen(true);
      setTimeout(() => setTimeout(() => endPickerRef.current?.setFocus()));
    },
    [end, onChange, restartOnStartSelect],
  );

  const startField = (
    <UDDateInput
      {...startProps}
      className={classNames('w-full', startProps?.className)}
      wrapperClassName={classNames('w-full', startProps?.wrapperClassName)}
      selected={start}
      onChange={handleStartChange}
      onSelect={handleStartSelect}
      calendarMotion={isHandoff ? HANDOFF_START_MOTION : undefined}
      selectsStart
      startDate={start}
      endDate={end}
      openToDate={start ?? end ?? undefined}
      minDate={min}
      maxDate={restartOnStartSelect ? max : (end ?? max)}
      disabled={disabled}
      hasError={hasErrorStart}
      valid={validStart}
    />
  );

  const endField = (
    <UDDateInput
      {...endProps}
      className={classNames('w-full', endProps?.className)}
      wrapperClassName={classNames('w-full', endProps?.wrapperClassName)}
      pickerRef={endPickerRef}
      popperModifiers={endPopperModifiers}
      calendarMotion={isHandoff ? HANDOFF_END_MOTION : undefined}
      selected={end}
      onChange={handleEndChange}
      selectsEnd
      startDate={start}
      endDate={end}
      openToDate={end ?? start ?? undefined}
      minDate={start ?? min}
      maxDate={max}
      disabled={disabled}
      hasError={hasErrorEnd}
      valid={validEnd}
    />
  );

  return (
    <div className={classNames('grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-x-2', className)}>
      <div ref={startColumnRef} className="min-w-0">
        {renderField('start', startField)}
      </div>
      <span aria-hidden className="text-inkMuted flex h-9 items-center">
        —
      </span>
      <div ref={endColumnRef} className="min-w-0">
        {renderField('end', endField)}
      </div>
    </div>
  );
};

export default UDDateRangeInput;
